import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';
import SeoContent from '../components/SeoContent';

/* Each puzzle = [H1, H2, H3, V1, V2, V3]
   H1/H2/H3 = rows 0/2/4 ; V1/V2/V3 = cols 0/2/4. All interlock at shared cells. */
const PUZZLES = [
  ['BOARD','STUNG','SPENT','BASES','ACUTE','DIGIT'],
  ['SHOUT','ABOVE','FRESH','SCARF','OZONE','TEETH'],
  ['ULTRA','SHIRE','TREND','UPSET','TRIPE','AHEAD'],
  ['MIDST','DRANK','LEARN','MEDAL','DRAMA','TOKEN'],
  ['MUSIC','ABOVE','TUMOR','MEANT','STORM','CHEER'],
  ['SCOPE','EAGER','RANCH','SWEAR','ORGAN','EARTH'],
  ['TOPIC','SHAKE','ENTER','TASTE','PLANT','CHEER'],
  ['DANCE','ORBIT','EVERY','DROVE','NOBLE','ENTRY'],
  ['SMELL','TUTOR','NURSE','SATIN','ENTER','LARGE'],
  ['STEAM','ALTER','TRAIL','SHAFT','EXTRA','MORAL'],
  ['THERE','TWICE','EVENT','TITLE','ELITE','ELECT'],
  ['PRESS','SHAVE','AFTER','PASTA','EXACT','SHEER'],
  ['LEAST','TIGER','SERVE','LOTUS','ANGER','THREE'],
  ['STEAL','AUDIO','THREE','START','ELDER','LOOSE'],
  ['CLOWN','LIGHT','RANCH','COLOR','ORGAN','NOTCH'],
  ['SMOKE','ELECT','PLAIN','SLEEP','OPERA','EATEN'],
  ['TRAMP','THERE','LEAVE','TOTAL','ARENA','PIECE'],
  ['SCENE','AWARE','ESSAY','STATE','EXAMS','ENEMY'],
  ['CORAL','LIVER','RINSE','COLOR','RAVEN','LARGE'],
  ['DRANK','BRACE','TREAT','DEBUT','AWARE','KNELT'],
  ['BRACE','SNAKE','SUNNY','BASES','AGAIN','ENEMY'],
  ['ALERT','OFTEN','TORCH','ABOUT','ENTER','TENTH'],
  ['LEAVE','THESE','SMART','LOTUS','ARENA','EVENT'],
  ['BASIC','SMOKE','SLEPT','BASES','SCORE','CHEST'],
  ['GRASS','OZONE','EVENT','GROVE','ALONE','SCENT'],
  ['DIGIT','ARISE','AHEAD','DRAMA','GUIDE','TREND'],
  ['HAUNT','ACTOR','TRADE','HEART','ULTRA','THREE'],
  ['SAINT','LEASE','THERE','SPLIT','IMAGE','THEME'],
  ['FORTH','RIDER','MARSH','FORUM','RADAR','HARSH'],
  ['HORSE','APPLE','TODAY','HEART','RAPID','ENEMY'],
  ['CHIEF','APART','STEEL','CLASS','IMAGE','FATAL'],
  ['WHERE','TUTOR','HEAVY','WATCH','EXTRA','EARLY'],
  ['LEAST','GIANT','THESE','LIGHT','AWARE','TITLE'],
  ['DEALT','CRIME','YIELD','DECAY','ASIDE','TREND'],
  ['LEAST','STOUT','RIDER','LOSER','ALOUD','TUTOR'],
  ['ADOPT','ELITE','AGENT','ARENA','OLIVE','TREAT'],
  ['AUDIT','GROAN','RANCH','ANGER','DROWN','TENTH'],
  ['BREED','OUGHT','NORTH','BLOWN','EAGER','DITCH'],
  ['FLUID','EXTRA','HEART','FLESH','ULTRA','DEALT'],
  ['STERN','ENTER','LEAVE','SHELL','EXTRA','NERVE'],
  ['SKATE','APART','LINER','SHALL','AGAIN','ENTER'],
  ['CRAFT','EXAMS','RANGE','CHEER','AGAIN','TASTE'],
  ['ASSET','GRASP','LINER','ANGEL','STAIN','TAPER'],
  ['UPSET','HELLO','RANGE','USHER','SALON','THOSE'],
  ['SNACK','NERVE','EVENT','SENSE','AGREE','KNELT'],
  ['LEAVE','NOISE','SLEPT','LINKS','ASIDE','ELECT'],
];

const ALLOWANCE = 15;       // total swaps allowed
const SCRAMBLE_SWAPS = 12;  // random swaps applied to scramble (<= ALLOWANCE)

// playable cells = all of 5x5 except the 4 holes
const HOLES = new Set(['1,1', '1,3', '3,1', '3,3']);
const POSITIONS = [];
for (let r = 0; r < 5; r++)
  for (let c = 0; c < 5; c++)
    if (!HOLES.has(`${r},${c}`)) POSITIONS.push([r, c]);

// the 6 word-lines as arrays of [r,c]
const LINES = [
  [[0,0],[0,1],[0,2],[0,3],[0,4]], // H1
  [[2,0],[2,1],[2,2],[2,3],[2,4]], // H2
  [[4,0],[4,1],[4,2],[4,3],[4,4]], // H3
  [[0,0],[1,0],[2,0],[3,0],[4,0]], // V1
  [[0,2],[1,2],[2,2],[3,2],[4,2]], // V2
  [[0,4],[1,4],[2,4],[3,4],[4,4]], // V3
];

function buildSolution(p) {
  const [h1, h2, h3, v1, v2, v3] = p;
  const g = Array.from({ length: 5 }, () => Array(5).fill(null));
  for (let i = 0; i < 5; i++) {
    g[0][i] = h1[i];
    g[2][i] = h2[i];
    g[4][i] = h3[i];
    g[i][0] = v1[i];
    g[i][2] = v2[i];
    g[i][4] = v3[i];
  }
  return g;
}

function scramble(solution) {
  // collect the 21 letters then shuffle by random valid swaps
  const letters = POSITIONS.map(([r, c]) => solution[r][c]);
  for (let s = 0; s < SCRAMBLE_SWAPS; s++) {
    let i = Math.floor(Math.random() * letters.length);
    let j = Math.floor(Math.random() * letters.length);
    // avoid no-op swaps between identical letters
    let guard = 0;
    while (letters[i] === letters[j] && guard++ < 20) {
      j = Math.floor(Math.random() * letters.length);
    }
    [letters[i], letters[j]] = [letters[j], letters[i]];
  }
  const g = Array.from({ length: 5 }, () => Array(5).fill(null));
  POSITIONS.forEach(([r, c], idx) => { g[r][c] = letters[idx]; });
  return g;
}

// Wordle-style colouring across each of the 6 lines, merged per cell.
function computeColors(board, solution) {
  const rank = { gray: 0, yellow: 1, green: 2 };
  const colors = {};
  POSITIONS.forEach(([r, c]) => { colors[`${r},${c}`] = 'gray'; });

  for (const line of LINES) {
    const cur = line.map(([r, c]) => board[r][c]);
    const sol = line.map(([r, c]) => solution[r][c]);
    const lineColor = Array(5).fill('gray');
    const pool = {};
    // greens first
    for (let i = 0; i < 5; i++) {
      if (cur[i] === sol[i]) lineColor[i] = 'green';
      else pool[sol[i]] = (pool[sol[i]] || 0) + 1;
    }
    // yellows from remaining pool
    for (let i = 0; i < 5; i++) {
      if (lineColor[i] === 'green') continue;
      if (pool[cur[i]] > 0) { lineColor[i] = 'yellow'; pool[cur[i]]--; }
    }
    // merge into cell map (best colour wins)
    line.forEach(([r, c], i) => {
      const key = `${r},${c}`;
      if (rank[lineColor[i]] > rank[colors[key]]) colors[key] = lineColor[i];
    });
  }
  return colors;
}

const COLOR_BG = { green: '#6aaa64', yellow: '#c9b458', gray: '#cbd5e1' };
const COLOR_FG = { green: '#fff', yellow: '#fff', gray: '#475569' };

export default function WaffleGame() {
  const [pIndex, setPIndex]   = useState(() => Math.floor(Math.random() * PUZZLES.length));
  const [solution, setSolution] = useState(() => buildSolution(PUZZLES[0]));
  const [board, setBoard]     = useState(() => buildSolution(PUZZLES[0]));
  const [selected, setSelected] = useState(null);
  const [swapsUsed, setSwapsUsed] = useState(0);
  const [phase, setPhase]     = useState('idle'); // idle | playing | won | lost
  const [showShare, setShowShare] = useState(false);
  const [revealed, setRevealed]   = useState(false); // solution shown on board
  const [revealHint, setRevealHint] = useState(false); // "use all swaps first" note

  const startPuzzle = useCallback((idx) => {
    const p = PUZZLES[idx];
    const sol = buildSolution(p);
    let scr = scramble(sol);
    // guard against an accidental near-solved scramble
    let tries = 0;
    while (tries++ < 8) {
      const colors = computeColors(scr, sol);
      const greens = Object.values(colors).filter(c => c === 'green').length;
      if (greens <= 12) break;
      scr = scramble(sol);
    }
    setSolution(sol);
    setBoard(scr);
    setSelected(null);
    setSwapsUsed(0);
    setRevealed(false);
    setRevealHint(false);
    setPhase('playing');
  }, []);

  const newGame = useCallback(() => {
    const next = (pIndex + 1 + Math.floor(Math.random() * (PUZZLES.length - 1))) % PUZZLES.length;
    setPIndex(next);
    startPuzzle(next);
  }, [pIndex, startPuzzle]);

  const colors = useMemo(() => computeColors(board, solution), [board, solution]);

  // When the player taps "Reveal Solution" (only allowed after all swaps are used),
  // show the real solution on the board (all green). Otherwise show the live board.
  // The shared emoji grid always keeps the player's true attempt.
  const displayBoard = revealed ? solution : board;
  const displayColors = useMemo(() => {
    if (!revealed) return colors;
    const all = {};
    POSITIONS.forEach(([r, c]) => { all[`${r},${c}`] = 'green'; });
    return all;
  }, [revealed, colors]);

  const isSolved = useMemo(
    () => POSITIONS.every(([r, c]) => board[r][c] === solution[r][c]),
    [board, solution]
  );

  useEffect(() => {
    if (phase !== 'playing') return;
    if (isSolved) { setPhase('won'); setTimeout(() => setShowShare(false), 0); return; }
    if (swapsUsed >= ALLOWANCE) setPhase('lost');
  }, [isSolved, swapsUsed, phase]);

  const swapsLeft = ALLOWANCE - swapsUsed;
  const stars = phase === 'won' ? Math.min(5, swapsLeft) : 0;

  const handleReveal = useCallback(() => {
    if (swapsLeft > 0) { setRevealHint(true); return; }  // must use all swaps first
    setRevealHint(false);
    setRevealed(true);
  }, [swapsLeft]);

  const clickCell = useCallback((r, c) => {
    if (phase !== 'playing') return;
    const key = `${r},${c}`;
    if (colors[key] === 'green') {           // greens are locked
      setSelected(null);
      return;
    }
    if (!selected) { setSelected(key); return; }
    if (selected === key) { setSelected(null); return; }

    const [sr, sc] = selected.split(',').map(Number);
    if (colors[selected] === 'green') { setSelected(key); return; }

    setBoard(prev => {
      const g = prev.map(row => [...row]);
      [g[r][c], g[sr][sc]] = [g[sr][sc], g[r][c]];
      return g;
    });
    setSwapsUsed(n => n + 1);
    setSelected(null);
  }, [phase, selected, colors]);

  const greenCount = useMemo(
    () => Object.values(colors).filter(c => c === 'green').length, [colors]
  );

  // Emoji grid for sharing (final colours, waffle shape)
  const emojiGrid = useMemo(() => {
    const EM = { green: '🟩', yellow: '🟨', gray: '⬜' };
    const rows = [];
    for (let r = 0; r < 5; r++) {
      let line = '';
      for (let c = 0; c < 5; c++) {
        line += HOLES.has(`${r},${c}`) ? '⬛' : EM[colors[`${r},${c}`]];
      }
      rows.push(line);
    }
    return rows.join('\n');
  }, [colors]);

  const shareText = [
    `Waffle Word Game 🧇`,
    phase === 'won' ? `Solved with ${swapsLeft} swap${swapsLeft === 1 ? '' : 's'} to spare!` : `So close — out of swaps!`,
    phase === 'won' ? '⭐'.repeat(stars) + '☆'.repeat(5 - stars) : '',
    ``,
    emojiGrid,
    ``,
    `Play free at wordzingo.com/waffle`,
  ].filter(l => l !== null).join('\n');

  const swapColor = swapsLeft > 6 ? '#16a34a' : swapsLeft > 3 ? '#d97706' : '#dc2626';

  return (
    <div className="game-page">
      <PageSEO page="waffle" />

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 1rem' }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        {/* Header */}
        <div className="text-center mb-3">
          <h1 style={{
            fontFamily: 'Nunito, sans-serif', fontWeight: 900,
            fontSize: 'clamp(2rem, 6vw, 2.8rem)', color: '#1e1b4b', margin: 0,
          }}>
            🧇 Waffle Word Game
          </h1>
          <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', margin: '0.3rem 0 0' }}>
            Swap the tiles to spell all 6 words. You get {ALLOWANCE} swaps — green tiles are locked in place!
          </p>
        </div>

        {/* ── IDLE ── */}
        {phase === 'idle' && (
          <div className="text-center">
            <div style={{
              background: '#fff', borderRadius: 20, padding: '2rem',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: '1.5rem',
            }}>
              <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: '#1e1b4b', marginBottom: '1.25rem' }}>
                How to Play
              </h3>
              <div className="row g-3 text-start mb-2">
                {[
                  ['🔀', 'Tap two tiles to swap their letters'],
                  ['🟩', 'Green = correct spot (it locks!)'],
                  ['🟨', 'Yellow = right word, wrong spot'],
                  ['⬜', 'Gray = not in that word'],
                  ['🧇', 'Spell all 3 across + 3 down words'],
                  ['⭐', `Finish under ${ALLOWANCE} swaps to earn stars`],
                ].map(([icon, text]) => (
                  <div key={text} className="col-12 col-sm-6">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
                      <span style={{ fontFamily: 'Nunito, sans-serif', color: '#374151', fontSize: '0.95rem' }}>{text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={() => startPuzzle(pIndex)} style={{
              background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 50,
              padding: '0.9rem 3.5rem', fontWeight: 900, fontFamily: 'Nunito, sans-serif',
              fontSize: '1.2rem', cursor: 'pointer', boxShadow: '0 4px 18px rgba(245,158,11,0.4)',
            }}>
              ▶ Start Game
            </button>
          </div>
        )}

        {/* ── PLAYING / WON / LOST ── */}
        {phase !== 'idle' && (
          <>
            {/* Stat row */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1, background: '#fff', borderRadius: 14, padding: '0.6rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <div style={statLabel}>Swaps Left</div>
                <div style={{ ...statVal, color: swapColor }}>{swapsLeft}</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: 14, padding: '0.6rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <div style={statLabel}>Tiles Set</div>
                <div style={{ ...statVal, color: '#6aaa64' }}>{greenCount}/21</div>
              </div>
            </div>

            {/* Waffle grid */}
            <div style={{
              background: '#fff', borderRadius: 20, padding: 'clamp(0.75rem, 4vw, 1.5rem)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.07)', marginBottom: '1rem',
            }}>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 'clamp(4px, 1.5vw, 8px)', maxWidth: 380, margin: '0 auto',
              }}>
                {displayBoard.map((row, r) => row.map((cell, c) => {
                  const key = `${r},${c}`;
                  if (HOLES.has(key)) return <div key={key} />;
                  const col = displayColors[key];
                  const isSel = selected === key;
                  const locked = col === 'green';
                  return (
                    <button
                      key={key}
                      onClick={() => clickCell(r, c)}
                      disabled={phase !== 'playing'}
                      style={{
                        aspectRatio: '1 / 1', width: '100%',
                        border: isSel ? '3px solid #1e1b4b' : '2px solid rgba(0,0,0,0.06)',
                        borderRadius: 12,
                        background: COLOR_BG[col], color: COLOR_FG[col],
                        fontFamily: 'Nunito, sans-serif', fontWeight: 900,
                        fontSize: 'clamp(1.1rem, 5vw, 1.7rem)',
                        cursor: phase === 'playing' && !locked ? 'pointer' : 'default',
                        transition: 'transform 0.12s, background 0.2s',
                        transform: isSel ? 'scale(1.08)' : 'scale(1)',
                        textTransform: 'uppercase', userSelect: 'none',
                        boxShadow: isSel ? '0 4px 14px rgba(30,27,75,0.3)' : 'none',
                      }}
                    >
                      {cell}
                    </button>
                  );
                }))}
              </div>
            </div>

            {phase === 'playing' && (
              <p style={{ textAlign: 'center', color: '#9ca3af', fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', marginBottom: '1rem' }}>
                {selected ? 'Tap another tile to swap →' : 'Tap a tile to start a swap'}
              </p>
            )}

            {/* Win / Lose banner */}
            {(phase === 'won' || phase === 'lost') && (
              <div style={{
                background: '#fff', borderRadius: 20, padding: '1.75rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '1rem', textAlign: 'center',
              }}>
                <div style={{ fontSize: '2.6rem', marginBottom: '0.3rem' }}>
                  {phase === 'won' ? '🎉' : '😅'}
                </div>
                <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 900, color: '#1e1b4b', fontSize: '1.7rem', marginBottom: '0.3rem' }}>
                  {phase === 'won' ? 'Waffle Solved!' : 'Out of Swaps!'}
                </h2>
                {phase === 'won' ? (
                  <>
                    <div style={{ fontSize: '1.8rem', letterSpacing: '0.1em', margin: '0.4rem 0' }}>
                      {'⭐'.repeat(stars)}{'☆'.repeat(5 - stars)}
                    </div>
                    <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', marginBottom: '1.25rem' }}>
                      Solved with <strong>{swapsLeft}</strong> swap{swapsLeft === 1 ? '' : 's'} to spare.
                    </p>
                  </>
                ) : (
                  <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif', marginBottom: '1.25rem' }}>
                    {revealed
                      ? 'Here is the full solution shown above — try a fresh waffle!'
                      : 'You used all your swaps. Reveal the solution or try a fresh waffle.'}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {phase === 'lost' && !revealed && (
                    <button onClick={handleReveal} style={primaryBtn}>👁 Reveal Solution</button>
                  )}
                  <button onClick={newGame} style={(phase === 'lost' && !revealed) ? outlineBtn : primaryBtn}>🔄 New Puzzle</button>
                  <button onClick={() => setShowShare(true)} style={outlineBtn}>📤 Share</button>
                </div>
              </div>
            )}

            {phase === 'playing' && (
              <>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <button onClick={newGame} style={outlineBtn}>🔄 New Puzzle</button>
                  <button onClick={handleReveal} style={outlineBtn}>👁 Reveal Solution</button>
                </div>
                {revealHint && (
                  <div style={{
                    background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: 12,
                    padding: '0.75rem 1rem', marginBottom: '1rem', textAlign: 'center',
                    color: '#92400e', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.92rem',
                  }}>
                    🔒 Use all {ALLOWANCE} swaps first — you have {swapsLeft} left. The solution unlocks once your swaps run out!
                  </div>
                )}
              </>
            )}
          </>
        )}

        {showShare && (
          <ShareModal
            shareText={shareText}
            gameUrl="https://wordzingo.com/waffle"
            onClose={() => setShowShare(false)}
            onNewGame={newGame}
          />
        )}

        <SeoContent game="waffle" />
      </div>
    </div>
  );
}

const statLabel = {
  fontSize: '0.65rem', color: '#9ca3af', fontFamily: 'Nunito, sans-serif',
  fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
};
const statVal = {
  fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.1,
};
const primaryBtn = {
  background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 50,
  padding: '0.7rem 2rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif',
  fontSize: '1rem', cursor: 'pointer', boxShadow: '0 3px 12px rgba(245,158,11,0.35)',
};
const outlineBtn = {
  background: '#fff', color: '#f59e0b', border: '2px solid #f59e0b', borderRadius: 50,
  padding: '0.7rem 2rem', fontWeight: 800, fontFamily: 'Nunito, sans-serif',
  fontSize: '1rem', cursor: 'pointer',
};
